import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Heart, ChevronLeft, ChevronRight, MapPin, Loader2 } from 'lucide-react'

interface Etapa2Props {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
}

export function Etapa2Responsavel({ formData, onChange, onNext, onPrev }: Etapa2Props) {
  const [emailError, setEmailError] = useState<string>('')
  const [buscandoCep, setBuscandoCep] = React.useState(false)
  const [cepInvalido, setCepInvalido] = React.useState(false)

  // useEffect para monitorar mudanças no CEP e buscar automaticamente
  React.useEffect(() => {
    const cepValue = formData.responsavel?.endereco?.cep || ''
    const cepNumeros = cepValue.replace('-', '')
    
    if (cepNumeros.length === 8 && !buscandoCep) {
      handleCepBlur(cepNumeros)
    }
  }, [formData.responsavel?.endereco?.cep])

  const handleCepBlur = async (cep: string) => {
    if (cep && cep.length === 8) {
      setBuscandoCep(true)
      setCepInvalido(false)
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
        const data = await response.json()
        
        if (!data.erro) {
          const enderecoAtualizado = {
            ...formData.responsavel?.endereco,
            cep: formData.responsavel?.endereco?.cep || '',
            rua: data.logradouro || '',
            bairro: data.bairro || '',
            cidade: data.localidade || '',
            estado: data.uf || '',
            complemento: data.complemento || ''
          }
          onChange('responsavel.endereco', enderecoAtualizado)
        } else {
          setCepInvalido(true)
        }
      } catch (error) {
        setCepInvalido(true)
      } finally {
        setBuscandoCep(false)
      }
    } else {
      setCepInvalido(false)
    }
  }

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    if (!formData.responsavel?.nome?.trim()) errors.push('Nome do responsável')
    if (formData.responsavel?.responsavel_financeiro === undefined) errors.push('Responsável financeiro')
    if (!formData.responsavel?.parentesco?.trim()) errors.push('Parentesco')
    if (!formData.responsavel?.whatsapp?.trim()) errors.push('WhatsApp')
    if (!formData.responsavel?.email?.trim()) errors.push('Email')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.responsavel?.email || '')) errors.push('Email inválido')
    if (!formData.responsavel?.endereco?.cep?.trim()) errors.push('CEP')
    if (!formData.responsavel?.endereco?.rua?.trim()) errors.push('Rua')
    if (!formData.responsavel?.endereco?.numero?.trim()) errors.push('Número')
    if (!formData.responsavel?.endereco?.bairro?.trim()) errors.push('Bairro')
    if (!formData.responsavel?.endereco?.cidade?.trim()) errors.push('Cidade')
    if (!formData.responsavel?.endereco?.estado?.trim()) errors.push('Estado')

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-blue-600" />
            Etapa 2: Responsável
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="responsavel_nome">Nome do Responsável *</Label>
            <Input
              id="responsavel_nome"
              value={formData.responsavel?.nome || ''}
              onChange={(e) => onChange('responsavel.nome', e.target.value)}
              placeholder="Nome completo do responsável"
              className="mt-1"
              required
            />
          </div>
          <div>
            <Label>Responsável Financeiro? *</Label>
            <Select 
              value={formData.responsavel?.responsavel_financeiro ? 'sim' : 'nao'} 
              onValueChange={(value) => onChange('responsavel.responsavel_financeiro', value === 'sim')}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Selecione Sim ou Não" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sim">Sim</SelectItem>
                <SelectItem value="nao">Não</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="parentesco">Parentesco *</Label>
            <Select 
              value={formData.responsavel?.parentesco || ''} 
              onValueChange={(value) => onChange('responsavel.parentesco', value)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="esposa">Esposa</SelectItem>
                <SelectItem value="marido">Marido</SelectItem>
                <SelectItem value="filha">Filha</SelectItem>
                <SelectItem value="filho">Filho</SelectItem>
                <SelectItem value="irma">Irmã</SelectItem>
                <SelectItem value="irmao">Irmão</SelectItem>
                <SelectItem value="mae">Mãe</SelectItem>
                <SelectItem value="pai">Pai</SelectItem>
                <SelectItem value="avo">Avó/Avô</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="whatsapp">WhatsApp *</Label>
            <Input
              id="whatsapp"
              type="tel"
              value={formData.responsavel?.whatsapp || ''}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, '')
                if (value.length > 11) value = value.slice(0, 11)
                
                if (value.length > 10) {
                  value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
                } else if (value.length > 6) {
                  value = value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
                } else if (value.length > 2) {
                  value = value.replace(/(\d{2})(\d{4,5})/, '($1) $2')
                }
                
                onChange('responsavel.whatsapp', value)
              }}
              placeholder="(00) 00000-0000"
              className="mt-1"
              maxLength={15}
              required
            />
          </div>
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.responsavel?.email || ''}
              onChange={(e) => {
                const email = e.target.value
                onChange('responsavel.email', email)
                
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                if (email && !emailRegex.test(email)) {
                  setEmailError('Por favor, insira um email válido')
                } else {
                  setEmailError('')
                }
              }}
              placeholder="email@exemplo.com"
              className={`mt-1 ${emailError ? 'border-red-500' : ''}`}
              required
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1">{emailError}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Endereço do Responsável */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Endereço do Responsável
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
              <Label htmlFor="cep" className="text-blue-900 font-semibold">CEP *</Label>
              <div className="relative mt-1">
                <Input
                  id="cep"
                  value={formData.responsavel?.endereco?.cep || ''}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '')
                    if (value.length > 8) value = value.slice(0, 8)
                    if (value.length > 5) {
                      value = value.replace(/(\d{5})(\d{3})/, '$1-$2')
                    }
                    onChange('responsavel.endereco', {
                      ...formData.responsavel?.endereco,
                      cep: value
                    })
                  }}
                  placeholder="00000-000"
                  className="bg-white pr-10 border-blue-200 focus:border-blue-400 focus:ring-blue-400"
                  maxLength={9}
                  disabled={buscandoCep}
                  required
                />
                {buscandoCep && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  </div>
                )}
              </div>
              <p className="text-[10px] text-blue-600 mt-1 font-medium">
                Puxa o endereço automaticamente ao digitar
              </p>
              {cepInvalido && (
                <p className="text-xs text-red-500 mt-1">CEP não encontrado. Verifique o número digitado.</p>
              )}
            </div>
            
            <div className="md:col-span-2 self-end">
              <Label htmlFor="rua">Rua/Avenida *</Label>
              <Input
                id="rua"
                value={formData.responsavel?.endereco?.rua || ''}
                onChange={(e) => onChange('responsavel.endereco', {
                  ...formData.responsavel?.endereco,
                  rua: e.target.value
                })}
                placeholder="Nome da rua"
                className="mt-1"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="numero">Número *</Label>
              <Input
                id="numero"
                value={formData.responsavel?.endereco?.numero || ''}
                onChange={(e) => onChange('responsavel.endereco', {
                  ...formData.responsavel?.endereco,
                  numero: e.target.value
                })}
                placeholder="Número"
                className="mt-1"
                required
              />
            </div>
            
            <div className="md:col-span-2">
              <Label htmlFor="complemento">Complemento</Label>
              <Input
                id="complemento"
                value={formData.responsavel?.endereco?.complemento || ''}
                onChange={(e) => onChange('responsavel.endereco', {
                  ...formData.responsavel?.endereco,
                  complemento: e.target.value
                })}
                placeholder="Apto, casa, fundos, etc."
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="bairro">Bairro *</Label>
              <Input
                id="bairro"
                value={formData.responsavel?.endereco?.bairro || ''}
                onChange={(e) => onChange('responsavel.endereco', {
                  ...formData.responsavel?.endereco,
                  bairro: e.target.value
                })}
                placeholder="Bairro"
                className="mt-1"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="cidade">Cidade *</Label>
              <Input
                id="cidade"
                value={formData.responsavel?.endereco?.cidade || ''}
                onChange={(e) => onChange('responsavel.endereco', {
                  ...formData.responsavel?.endereco,
                  cidade: e.target.value
                })}
                placeholder="Cidade"
                className="mt-1"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="estado">Estado *</Label>
              <Select 
                value={formData.responsavel?.endereco?.estado || ''} 
                onValueChange={(value) => onChange('responsavel.endereco', {
                  ...formData.responsavel?.endereco,
                  estado: value
                })}
                required
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="UF" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AC">AC</SelectItem>
                  <SelectItem value="AL">AL</SelectItem>
                  <SelectItem value="AP">AP</SelectItem>
                  <SelectItem value="AM">AM</SelectItem>
                  <SelectItem value="BA">BA</SelectItem>
                  <SelectItem value="CE">CE</SelectItem>
                  <SelectItem value="DF">DF</SelectItem>
                  <SelectItem value="ES">ES</SelectItem>
                  <SelectItem value="GO">GO</SelectItem>
                  <SelectItem value="MA">MA</SelectItem>
                  <SelectItem value="MT">MT</SelectItem>
                  <SelectItem value="MS">MS</SelectItem>
                  <SelectItem value="MG">MG</SelectItem>
                  <SelectItem value="PA">PA</SelectItem>
                  <SelectItem value="PB">PB</SelectItem>
                  <SelectItem value="PR">PR</SelectItem>
                  <SelectItem value="PE">PE</SelectItem>
                  <SelectItem value="PI">PI</SelectItem>
                  <SelectItem value="RJ">RJ</SelectItem>
                  <SelectItem value="RN">RN</SelectItem>
                  <SelectItem value="RS">RS</SelectItem>
                  <SelectItem value="RO">RO</SelectItem>
                  <SelectItem value="RR">RR</SelectItem>
                  <SelectItem value="SC">SC</SelectItem>
                  <SelectItem value="SP">SP</SelectItem>
                  <SelectItem value="SE">SE</SelectItem>
                  <SelectItem value="TO">TO</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
